import { useState } from "react";
import { useBancos, useCriarConta } from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useSessao } from "@/sessao/useSessao";
import { bancoDoCatalogo } from "./bancos";
import {
  VALORES_INICIAIS,
  camposDaConta,
  corpoDaCriacao,
  regrasDoBanco,
  validarConta,
  type CampoDaConta,
  type ErrosDaConta,
  type ValoresDaConta,
} from "./conta";
import { FormularioDeConta } from "./FormularioDeConta";
import { errosDoFormulario, textoDaRecusa } from "./recusas";

/*
 * Cadastro de conta bancária, aberto por `?abrir=nova-conta`.
 *
 * O cedente vem por parâmetro e aparece como dado do modal, e não é um campo de
 * escolha: a conta é criada a partir da página em que o operador está. Um seletor de
 * cedente aqui abriria a chance de cadastrar a conta de um cliente sob outro, que é o
 * erro mais caro desta tela: dinheiro chegando na conta errada.
 *
 * Fechar desmonta o modal, e por isso campos e recusa voltam limpos.
 */
export function NovaConta({
  pessoaID,
  pessoaNome,
  aoFechar,
}: {
  pessoaID: string;
  pessoaNome: string;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const criar = useCriarConta(cliente, pessoaID);
  const catalogo = useBancos(cliente, true);
  const bancos = catalogo.data?.bancos ?? [];

  const [valores, setValores] = useState<ValoresDaConta>(VALORES_INICIAIS);
  const [erros, setErros] = useState<ErrosDaConta>({});
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);

  const regras = regrasDoBanco(bancoDoCatalogo(bancos, valores.banco));

  function mudar(campo: CampoDaConta, valor: string) {
    setValores((atuais) => ({ ...atuais, [campo]: valor }));

    // Outro banco é outra regra: nenhum erro do anterior continua verdadeiro.
    if (campo === "banco") {
      setErros({});
      setErroGeral(undefined);
    } else {
      setErros((atuais) => ({ ...atuais, [campo]: undefined }));
    }
  }

  function enviar() {
    setErroGeral(undefined);
    const validacao = validarConta(valores, regras);
    if (!validacao.ok) {
      setErros(validacao.erros);
      return;
    }

    setErros({});
    criar.mutate(corpoDaCriacao(validacao.conta), {
      onSuccess: () => {
        avisos.feito("Conta cadastrada");
        aoFechar();
      },
      onError: (falha) => {
        const recusa = errosDoFormulario(falha, camposDaConta(regras));
        setErros(recusa.porCampo);
        setErroGeral(recusa.geral);
      },
    });
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Nova conta bancária"
      descricao={pessoaNome}
      aoEnviar={enviar}
      enviando={criar.isPending}
      erro={erroGeral}
    >
      <FormularioDeConta
        valores={valores}
        erros={erros}
        aoMudar={mudar}
        bancos={bancos}
        carregandoBancos={catalogo.isPending}
        falhaDosBancos={catalogo.error === null ? undefined : textoDaRecusa(catalogo.error)}
      />
    </ModalDeCadastro>
  );
}
